import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { format, addDays, parseISO, isValid } from 'date-fns';
import { cn } from '@/lib/utils';
import type { Room, Booking } from './BookingCalendar';
import { BookingDialog } from './BookingDialog';
import { EditBookingDialog } from './EditBookingDialog';
import { staffMembers } from './BookingCalendar';
import { useDevice } from '@/hooks/use-device';

interface DailyCalendarProps {
  rooms: Room[];
  bookings: Booking[];
  date: Date;
  onBookingCreate: (booking: Booking) => void;
  onBookingUpdate: (booking: Booking) => void;
  onBookingDelete: (bookingId: number) => void;
  onDayClick?: (date: Date) => void;
}

// Create time slots from 9:00 to 02:00 next day
const timeSlots = [
  ...Array.from({ length: 15 }, (_, i) => {
    const hour = i + 9;
    return `${hour.toString().padStart(2, '0')}:00`;
  }),
  '00:00',
  '01:00',
  '02:00'
];

const statusColors = {
  paid: 'bg-blue-100 border-l-4 border-blue-500 text-blue-800 hover:bg-blue-200/80 active:bg-blue-300/80 shadow-sm hover:shadow transition-all duration-200',
  booked: 'bg-emerald-100 border-l-4 border-emerald-500 text-emerald-800 hover:bg-emerald-200/80 active:bg-emerald-300/80 shadow-sm hover:shadow transition-all duration-200',
  pending: 'bg-amber-100 border-l-4 border-amber-500 text-amber-800 hover:bg-amber-200/80 active:bg-amber-300/80 shadow-sm hover:shadow transition-all duration-200'
};

// Helper function to safely parse date strings
const safeParseISO = (dateStr: string, timeStr: string): Date | null => {
  try {
    // Check if the date string already contains a time component (T)
    const cleanDateStr = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const dateTimeStr = `${cleanDateStr}T${timeStr}`;
    
    const parsedDate = parseISO(dateTimeStr);
    if (!isValid(parsedDate)) {
      console.warn(`Invalid date after parsing: ${dateTimeStr}`);
      return null;
    }
    return parsedDate;
  } catch (error) {
    console.error(`Error parsing date: ${dateStr} with time ${timeStr}`, error);
    return null;
  }
};

// Helper function to safely format dates
const safeFormatDate = (date: Date | null, formatStr: string, fallback: string = 'Invalid date'): string => {
  if (!date || !isValid(date)) return fallback;
  try {
    return format(date, formatStr);
  } catch (error) {
    console.error(`Error formatting date: ${date}`, error);
    return fallback;
  }
};

export default function DailyCalendar({
  rooms,
  bookings,
  date,
  onBookingCreate,
  onBookingUpdate,
  onBookingDelete,
  onDayClick
}: DailyCalendarProps) {
  const { isMobile, isTablet } = useDevice();
  const isCompactView = isMobile || isTablet;
  
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<{
    room: Room;
    hour: number;
    isNextDay: boolean;
  } | null>(null);

  const [selectedBooking, setSelectedBooking] = useState<{
    booking: Booking;
    room: Room;
  } | null>(null);

  const getBookingForSlot = (roomId: number, timeSlot: string) => {
    try {
      const hour = parseInt(timeSlot.split(':')[0]);
      const isNextDay = hour < 9;
      const bookingDateStr = isNextDay ? format(addDays(date, 1), 'yyyy-MM-dd') : format(date, 'yyyy-MM-dd');
      
      return bookings.find(booking => {
        if (booking.roomId !== roomId) return false;
        
        // Handle existing time component in date
        const bookingDateClean = booking.date.includes('T') ? booking.date.split('T')[0] : booking.date;
        if (bookingDateClean !== bookingDateStr) return false;
        
        const bookingHour = parseInt(booking.timeSlot.split(':')[0]);
        const bookingEndHour = bookingHour + booking.duration;
        const isBookingNextDay = bookingHour < 9;
        
        // Skip invalid bookings
        if (isNaN(bookingHour) || isNaN(bookingEndHour)) {
          console.warn(`Invalid booking time: ${booking.timeSlot} for booking ID ${booking.id}`);
          return false;
        }
        
        if (isNextDay) {
          return hour >= bookingHour && hour < bookingEndHour;
        } else {
          return hour >= bookingHour && hour < bookingEndHour;
        }
      });
    } catch (error) {
      console.error("Error in getBookingForSlot:", error);
      return undefined;
    }
  };

  const handleSlotClick = (room: Room, timeSlot: string) => {
    const hour = parseInt(timeSlot.split(':')[0]);
    const isNextDay = hour < 9;
    setSelectedSlot({
      room,
      hour,
      isNextDay
    });
  };

  const handleBookingClick = (booking: Booking, room: Room) => {
    setSelectedBooking({ booking, room });
  };

  const handleDuplicateComplete = (date: Date) => {
    onDayClick?.(date);
  };

  const getStaffColor = (name: string) => {
    const staff = staffMembers.find(s => s.name === name);
    return staff?.color || '';
  };
  
  const renderMobileView = () => {
    return (
      <div className="flex flex-col">
        {/* Room selector for mobile */}
        <div className="p-2 bg-gray-50 sticky top-0 z-10 border-b">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollable">
            <Button 
              variant={selectedRoom === null ? "default" : "outline"}
              size="sm" 
              onClick={() => setSelectedRoom(null)}
              className="whitespace-nowrap"
            >
              All Rooms
            </Button>
            {rooms.map(room => (
              <Button
                key={room.id}
                variant={selectedRoom?.id === room.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedRoom(room)}
                className={cn(
                  "whitespace-nowrap min-w-[120px]",
                  selectedRoom?.id === room.id ? "bg-purple-600" : ""
                )}
              >
                {room.name}
              </Button>
            ))}
          </div>
        </div>
        
        {/* Mobile time grid */}
        <div className="flex flex-col divide-y">
          {timeSlots.map(slot => {
            const hour = parseInt(slot);
            const isNextDay = hour < 9;
            const displayRooms = selectedRoom ? [selectedRoom] : rooms;
            
            return (
              <div key={slot} className={cn(
                "flex flex-col", 
                isNextDay ? "bg-gray-50" : "bg-white"
              )}>
                <div className="sticky left-0 bg-gray-100 p-2 font-medium text-sm">
                  {slot}
                  {isNextDay && <span className="text-xs text-gray-500 ml-2">(next day)</span>}
                </div>
                
                <div className="grid grid-cols-1 gap-2 p-2">
                  {displayRooms.map(room => {
                    const booking = getBookingForSlot(room.id, slot);
                    
                    if (booking && booking.timeSlot === slot) {
                      const staffColor = getStaffColor(booking.bookedBy);
                      
                      return (
                        <div 
                          key={`${room.id}-${slot}`}
                          className={cn(
                            "p-3 rounded-md min-h-[60px]",
                            statusColors[booking.status]
                          )}
                          onClick={() => handleBookingClick(booking, room)}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">{booking.clientName}</div>
                              <div className="text-sm">{room.name}</div>
                              <div className="text-xs mt-1">{booking.timeSlot} • {booking.duration}h</div>
                            </div>
                            <div className={cn(
                              "px-2 py-1 text-xs rounded-full",
                              staffColor
                            )}>
                              {booking.bookedBy}
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    if (booking) return null; // Already showing this booking
                    
                    return (
                      <Button
                        key={`${room.id}-${slot}`}
                        variant="ghost"
                        className="min-h-[48px] border border-dashed border-gray-200 justify-start items-start p-2 h-auto"
                        onClick={() => handleSlotClick(room, slot)}
                      >
                        <div className="text-xs text-gray-500 font-normal">
                          {room.name}
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTableCell = (room: Room, timeSlot: string) => {
    const booking = getBookingForSlot(room.id, timeSlot);
    const hour = parseInt(timeSlot.split(':')[0]);
    
    if (booking && booking.timeSlot !== timeSlot) {
      return null;
    }

    if (booking) {
      const staffColor = getStaffColor(booking.bookedBy);
      return (
        <td 
          key={`${room.id}-${timeSlot}`} 
          className="p-1 border-r border-b"
          colSpan={booking.duration}
        >
          <div 
            className={cn(
              "p-2 h-full min-h-[4rem] rounded cursor-pointer",
              statusColors[booking.status]
            )}
            onClick={() => handleBookingClick(booking, room)}
          >
            <div className="font-semibold">{booking.clientName}</div>
            <div className="text-sm">
              <span className={cn(
                "px-1.5 py-0.5 rounded-md transition-colors duration-200",
                staffColor
              )}>
                {booking.bookedBy}
              </span>
            </div>
            <div className="text-sm font-medium">{booking.duration}h</div>
          </div>
        </td>
      );
    }

    return (
      <td key={`${room.id}-${timeSlot}`} className="p-1 border-r border-b">
        <Button
          variant="ghost"
          size="sm"
          className="w-full h-16 bg-white hover:bg-gray-50/75 active:bg-gray-100/75 transition-all duration-200"
          onClick={() => handleSlotClick(room, timeSlot)}
        >
          <span className="sr-only">Book slot</span>
        </Button>
      </td>
    );
  };

  if (isCompactView) {
    return (
      <div className="bg-white rounded-lg overflow-hidden">
        {renderMobileView()}
        
        {selectedSlot && (
          <BookingDialog
            room={selectedSlot.room}
            date={selectedSlot.isNextDay ? addDays(date, 1) : date}
            hour={selectedSlot.hour}
            onClose={() => setSelectedSlot(null)}
            onSubmit={onBookingCreate}
          />
        )}

        {selectedBooking && (
          <EditBookingDialog
            booking={selectedBooking.booking}
            room={selectedBooking.room}
            onClose={() => setSelectedBooking(null)}
            onUpdate={onBookingUpdate}
            onDelete={onBookingDelete}
            onDuplicate={handleDuplicateComplete}
          />
        )}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto bg-white rounded-lg">
      <table className={cn(
        "w-full border-collapse",
        isCompactView ? "table-fixed" : "table-auto"
      )}>
        <colgroup>
          <col className={isCompactView ? "w-24" : "w-64"} />
          {timeSlots.map((_, index) => (
            <col key={index} className={isCompactView ? "w-[140px]" : "w-24"} />
          ))}
        </colgroup>
        <thead>
          <tr className="sticky top-0 z-10">
            <th className="p-3 border-r border-b bg-gray-100/80 backdrop-blur supports-[backdrop-filter]:bg-gray-100/50 text-left font-medium text-gray-600">Room</th>
            {timeSlots.map(slot => {
              const hour = parseInt(slot);
              const isNextDay = hour < 9;
              return (
                <th key={slot} className={cn(
                  "p-3 border-r border-b font-medium text-gray-600 text-sm",
                  isNextDay 
                    ? "bg-gray-200/80 backdrop-blur supports-[backdrop-filter]:bg-gray-200/50" 
                    : "bg-gray-100/80 backdrop-blur supports-[backdrop-filter]:bg-gray-100/50"
                )}>
                  {slot}
                  {isNextDay && <div className="text-xs text-gray-500">(next day)</div>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rooms.map(room => (
            <tr key={room.id} className="group">
              <td className="p-3 border-r border-b bg-gray-50 sticky left-0">
                <div className={cn("p-2.5 rounded-md shadow-sm", room.color)}>
                  <div className="font-medium">{room.name}</div>
                  <div className="text-sm opacity-75">Capacity: {room.capacity}</div>
                </div>
              </td>
              {timeSlots.map(slot => renderTableCell(room, slot))}
            </tr>
          ))}
        </tbody>
      </table>

      {selectedSlot && (
        <BookingDialog
          room={selectedSlot.room}
          date={selectedSlot.isNextDay ? addDays(date, 1) : date}
          hour={selectedSlot.hour}
          onClose={() => setSelectedSlot(null)}
          onSubmit={onBookingCreate}
        />
      )}

      {selectedBooking && (
        <EditBookingDialog
          booking={selectedBooking.booking}
          room={selectedBooking.room}
          onClose={() => setSelectedBooking(null)}
          onUpdate={onBookingUpdate}
          onDelete={onBookingDelete}
          onDuplicate={handleDuplicateComplete}
        />
      )}
    </div>
  );
}